// PusherEmitter.jsx
// This component used to decide the timing to emit pusher event.

import React from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

import env from '../resource/env.js';
import { getPusherFetchHeaders } from '../resource/fetchOptionHeader.js';
import * as MessageTypes from '../serviceWorker/AppMessageTypes.js';
import {
  initPusher,
  sendMessageToSW,
  resubscribeChannels,
} from '../serviceWorker/helpers.js';
import { serviceWorkerLog as serviceWorkerDebug } from '../resource/debug.js';
import trackMixpanel, {
  EventTypes,
  processMixpanelApiCall,
} from '../resource/mixpanel.js';
import ServiceWorkerMessenger from '../resource/ServiceWorkerMessenger.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getIsXUserAgent } from '../resource/getUserAgent.js';

const isOnWebview = getIsXUserAgent();

const chinaPusherActivityTimeout = 15 * 1000;
const defaultPusherActivityTimeout = 120 * 1000;
const isProd = 'production' === env.NODE_ENV;

const serviceWorkerDebugLog = serviceWorkerDebug.extend('PusherEmitter');

export class PusherEmitter extends React.PureComponent {
  pendingChannelsTimer = null;
  pendingChannels = new Set();
  sentPendingChannels = new Set();
  serviceWorkerMessenger = null;

  initPusher = () => {
    const { dispatch, isRemoteConfigMerged } = this.props;
    if (isRemoteConfigMerged) {
      const pusherConfig = this.getPusherConfig();
      initPusher({ dispatch, pusherConfig });
    }
  };

  componentDidMount() {
    const { isAuthed } = this.props;

    const pusherConfig = this.getPusherConfig();
    this.initPusher();

    this.serviceWorkerMessenger = ServiceWorkerMessenger.getInstance();
    globalThis._serviceWorker = this.serviceWorkerMessenger;

    // jest will complain missing serviceWorkerMessenger
    this.serviceWorkerMessenger
      ?.set({
        selectPath: ['scope', 'pusher', 'config'],
        value: pusherConfig,
        options: {
          isCritical: true,
          objectDepth: 2,
        },
      })
      .catch(error => {
        serviceWorkerDebugLog('set pusherConfig error', {
          pusherConfig,
          error,
        });
      });
    const applySyncPusherArgument = {
      selectPath: ['syncPusherTarget'],
      thisSelectPath: [],
      args: [],
      options: {
        isCritical: true,
        objectDepth: 2,
      },
    };
    // jest will complain missing serviceWorkerMessenger
    this.serviceWorkerMessenger?.apply(applySyncPusherArgument).catch(error => {
      serviceWorkerDebugLog('apply syncPusherTarget() error', {
        applySyncPusherArgument,
        error,
      });
    });

    if (isAuthed) {
      sendMessageToSW(this.getLoginMessageData());
    }
  }

  getPusherConfig = () => {
    const {
      country,
      language,
      token,
      pusherWssHost,
      pusherWsHost,
      pusherWssPort,
      pusherEnabledTransports,
      pusherAppId,
      pusherKey,
      pusherCluster,
    } = this.props;
    const isInChinaCountry = country === 'cn';
    const headers = getPusherFetchHeaders({
      token,
      clientId: this.getClientId(),
    });
    const pusherConfig = {
      authEndpoint: getResourceUrl({ endpoint: '/pusher/batch-authenticate' })
        .href,
      language,
      headers,
      appId: pusherAppId || env.PUSHER_APP_ID,
      key: pusherKey || env.PUSHER_KEY,
      cluster: pusherCluster || env.PUSHER_CLUSTER,
      shouldUsePusherOnServiceWorkerEnv:
        env.PUSHER_ON_SERVICE_WORKER === 'true',
      authBatchMaxCount: env.PUSHER_BATCH_MAX_COUNT,
      activityTimeout: isInChinaCountry
        ? chinaPusherActivityTimeout
        : defaultPusherActivityTimeout,
      reaperInterval: +(env.PUSHER_REAPER_INTERVAL || 2000),
      resourceUrl: isOnWebview ? window.__RESOURCE_URL__ : null,
    };

    if (pusherWssHost) pusherConfig.wssHost = pusherWssHost;
    if (pusherWsHost) pusherConfig.wsHost = pusherWsHost;
    if (pusherWssPort) pusherConfig.wssPort = pusherWssPort;
    if (pusherEnabledTransports.length)
      pusherConfig.enabledTransports = pusherEnabledTransports;

    return pusherConfig;
  };

  // we don't handle pusher AppId/Key/Cluster on update since we don't want re-connect.
  componentDidUpdate(prevProps) {
    const {
      isAuthed,
      isRemoteConfigMerged,
      token,
      userId,
      updatePusherDistinctId,
      pusherStalledChannelCheckInterval,
    } = this.props;

    if (prevProps.isRemoteConfigMerged !== isRemoteConfigMerged) {
      this.initPusher();
    }

    // login
    if (userId && prevProps.userId !== userId) {
      processMixpanelApiCall({ apiNames: ['identify'], args: [userId] });
      if (!prevProps.userId) {
        processMixpanelApiCall({ apiNames: ['people', 'set'], args: [{}] });
      }
    }

    // login or logout
    if (prevProps.userId !== userId) {
      updatePusherDistinctId();
    }

    if (isAuthed !== prevProps.isAuthed && isAuthed) {
      sendMessageToSW(this.getLoginMessageData());
    }

    if (token !== prevProps.token && token && prevProps.token) {
      // when token changed, we should re-subscribed presence channels
      sendMessageToSW(this.getLoginMessageData());
    }

    if (isProd) {
      this.checkPusherStatus();

      if (
        pusherStalledChannelCheckInterval !==
        prevProps.pusherStalledChannelCheckInterval
      ) {
        clearInterval(this.pendingChannelsTimer);
        this.pendingChannelsTimer = null;
      }

      this.checkPusherPendingChannels();
    }
  }

  componentWillUnmount() {
    clearInterval(this.pendingChannelsTimer);
  }

  // since the service worker updates the redux state according to the connection state of pusher
  // check the error state from redux to reconnect pusher
  checkPusherStatus = () => {
    const { serviceWorkerStatus } = this.props;
    const { error } = serviceWorkerStatus;

    if (error === 'pusher-connection-failed') {
      const pusherConfig = this.getPusherConfig();

      serviceWorkerDebugLog('reconnect because pusher-connection-failed', {
        serviceWorkerStatus,
        pusherConfig,
      });

      this.reConnectPusher(pusherConfig);
    }
  };

  reConnectPusher = pusherConfig => {
    const { updatePusherConnectionData, isAuthed, updateServiceWorkStatus } =
      this.props;
    updatePusherConnectionData({
      data: {
        state: 'disconnected',
      },
    });

    sendMessageToSW({
      type: MessageTypes.DISCONNECT_PUSHER,
    });

    sendMessageToSW({
      type: MessageTypes.INIT_PUSHER,
      payload: { pusherConfig },
    });

    if (isAuthed) sendMessageToSW(this.getLoginMessageData());

    resubscribeChannels();

    const now = Date.now();
    updateServiceWorkStatus({
      ping: now,
      pong: now,
      error: '',
    });
  };

  getLoginMessageData = () => {
    const { token, userId } = this.props;
    const headers = getPusherFetchHeaders({
      token,
      clientId: this.getClientId(),
    });
    return {
      type: MessageTypes.LOGIN,
      payload: { userId, headers },
    };
  };

  getClientId = () => {
    const { clientId, mergeMeData } = this.props;
    if (clientId) {
      return clientId;
    } else {
      const clientId = uuidv4();
      mergeMeData({ field: 'clientId', value: clientId });
      return clientId;
    }
  };

  checkPusherPendingChannels = () => {
    const {
      pusherStalledChannelCheckInterval,
      pusherStalledChannelCheckLimit,
    } = this.props;

    if (
      this.pendingChannelsTimer ||
      this.sentPendingChannels.size >= pusherStalledChannelCheckLimit
    )
      return;

    this.pendingChannelsTimer = setInterval(() => {
      const {
        pusherChannels,
        pusherConnectionState,
        pusherStalledChannelCheckLimit: latestPusherStalledChannelCheckLimit,
      } = this.props;

      Object.keys(pusherChannels).forEach(channelName => {
        const channel = pusherChannels[channelName];
        const isSubscriptionPending = channel?.subscriptionPending;

        if (
          channel &&
          isSubscriptionPending &&
          this.pendingChannels.has(channelName) &&
          !this.sentPendingChannels.has(channelName)
        ) {
          trackMixpanel({
            type: EventTypes.PUSHER_CHANNEL_STALLED,
            payload: {
              channelName,
              subscribed: channel.subscribed,
              subscriptionCancelled: channel.subscriptionCancelled,
              subscriptionPending: channel.subscriptionPending,
              connectionState: pusherConnectionState,
            },
          });

          this.sentPendingChannels.add(channelName);
        } else {
          this.pendingChannels.has(channelName)
            ? this.pendingChannels.delete(channelName)
            : isSubscriptionPending && this.pendingChannels.add(channelName);
        }
      });

      if (this.sentPendingChannels.size >= latestPusherStalledChannelCheckLimit)
        clearInterval(this.pendingChannelsTimer);
    }, pusherStalledChannelCheckInterval);
  };

  render() {
    return null;
  }
}

PusherEmitter.propTypes = {
  token: PropTypes.string,
  isRemoteConfigMerged: PropTypes.bool,
  isAuthed: PropTypes.bool,
  pusherWssHost: PropTypes.string,
  pusherWsHost: PropTypes.string,
  pusherWssPort: PropTypes.string,
  pusherEnabledTransports: PropTypes.array,
  pusherAppId: PropTypes.string,
  pusherKey: PropTypes.string,
  pusherCluster: PropTypes.string,
  userId: PropTypes.string,
  clientId: PropTypes.string,
  country: PropTypes.string,
  language: PropTypes.string,
  mergeMeData: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
  serviceWorkerStatus: PropTypes.object,
  updateServiceWorkStatus: PropTypes.func,
  updatePusherConnectionData: PropTypes.func,
  updatePusherDistinctId: PropTypes.func,
  pusherChannels: PropTypes.object,
  pusherConnectionState: PropTypes.string,
  pusherStalledChannelCheckInterval: PropTypes.number,
  pusherStalledChannelCheckLimit: PropTypes.number,
};

PusherEmitter.defaultProps = {
  token: '',
  isRemoteConfigMerged: false,
  isAuthed: false,
  pusherWssHost: null,
  pusherWsHost: null,
  pusherWssPort: null,
  pusherEnabledTransports: [],
  pusherAppId: '',
  pusherKey: '',
  pusherCluster: '',
  userId: '',
  clientId: '',
  country: '',
  language: '',
  mergeMeData: () => null,
  serviceWorkerStatus: {
    ping: Date.now(),
    pong: Date.now(),
    error: '',
  },
  updateServiceWorkStatus: () => null,
  updatePusherConnectionData: () => null,
  updatePusherDistinctId: () => null,
  pusherChannels: {},
  pusherConnectionState: '',
  pusherStalledChannelCheckInterval: 10000,
  pusherStalledChannelCheckLimit: 32,
};

export default PusherEmitter;
